import { Flex, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';

import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NotificationService from 'core/services/notification';
import { onGetPointWalletBalance, onGetPointWalletTransactions } from 'modules/users/services/users';
import { PointWalletBalance, PointWalletTransaction } from 'modules/users/models/point-wallet-transactions';
import styles from '../../pages/view/view.module.scss';
import { getTopPosition } from 'utils/elementTopPosition';
import { useTranslation } from 'react-i18next';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

interface InputProps {
	id: number;
}

function PointWalletTransactionsComponent({ id }: InputProps) {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [pointWalletTransactions, setPointWalletTransactions] = useState<PointWalletTransaction[]>([]);
	const [pointWalletBalance, setPointWalletBalance] = useState<PointWalletBalance>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	useEffect(() => {
		getData();
	}, []);

	const columns: TableProps<PointWalletTransaction>['columns'] = [
		{
			title: '#',
			dataIndex: '#',
			key: '#',
			render: (id, transaction, index) => {
				const actualIndex = (currentPage - 1) * 15 + index + 1;
				return <span style={{ fontWeight: 500, color: '#3F4254' }}>{actualIndex}</span>;
			},
		},
		{
			title: t('USERS.AMOUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (amount) => (
				<Flex className="priceTag" gap={5} align="center" style={{ fontWeight: 500, color: '#7E8299' }}>
					{amount}
					<Icon name="riyal" width={13} height={13} color="#7E8299" />
				</Flex>
			),
		},
		{
			title: t('USERS.BASE_AMOUNT'),
			dataIndex: 'base_amount',
			key: 'base_amount',
			render: (base_amount) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{base_amount}</span>,
		},
		{
			title: t('USERS.VAT_AMOUNT'),
			dataIndex: 'vat_amount',
			key: 'vat_amount',
			render: (vat_amount) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{vat_amount}</span>,
		},
		{
			title: t('USERS.TYPE'),
			dataIndex: 'type',
			key: 'type',
			render: (type) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{type}</span>,
		},
		{
			title: t('USERS.DESCRIPTION'),
			dataIndex: 'description',
			key: 'description',
			render: (description) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{description}</span>,
		},
		{
			title: t('USERS.DATE'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{date}</span>,
		},
	];

	const getData = async () => {
		try {
			setIsLoading(true);

			const transactionsResponse = await onGetPointWalletTransactions(id);
			setPointWalletTransactions(
				transactionsResponse.data.data.user_point_wallet_transactions.map((el, i) => {
					return { ...el, id: i };
				}),
			);
			setTotalRecords(transactionsResponse.data.data.pagination.total);

			const balanceResponse = await onGetPointWalletBalance(id);
			setPointWalletBalance(balanceResponse.data.data);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);

			const response = await onGetPointWalletTransactions(id, page);
			setPointWalletTransactions(
				response.data.data.user_point_wallet_transactions.map((el, i) => {
					return { ...el, id: i };
				}),
			);

			setTimeout(() => {
				window.scrollTo({ behavior: 'smooth', top: getTopPosition('point-wallet-transactions-table') - 65 });
			});

			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	return (
		<>
			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className={styles.data_details}>
						<div className="px-3">
							<span>{t('USERS.AVAILABLE_WITHDRAW')}</span>
							<p className="priceTag">
								{pointWalletBalance?.available_withdraw_able}
								<Icon name="riyal" width={13} height={13} />
							</p>
						</div>
						<div className="px-3">
							<span>{t('USERS.LAST_UPDATE')}</span>
							<p>{pointWalletBalance?.last_update ? pointWalletBalance?.last_update : t('COMMON.N_A')}</p>
						</div>
					</div>

					<div style={{ position: 'relative' }}>
						<Table
							id="point-wallet-transactions-table"
							rowKey="id"
							columns={columns}
							dataSource={pointWalletTransactions}
							scroll={{ x: 400 }}
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</>
			)}
		</>
	);
}

export default PointWalletTransactionsComponent;
